/**
 * Textarea — auto-growing multi-line field with an optional character counter.
 *
 * Growth is capped at `maxRows`; past that the field scrolls internally rather
 * than pushing the rest of the form down the page.
 */

import { forwardRef, useCallback, useLayoutEffect, useRef, useState } from 'react';
import type { TextareaHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';
import { composeRefs } from './Button';
import type { ControlSize } from './Button';
import { fieldShell } from './Input';

const PAD: Record<ControlSize, string> = {
  sm: 'px-1.5 py-1',
  md: 'px-2 py-1.5',
  lg: 'px-2.5 py-2',
};

export interface TextareaProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'size' | 'rows'> {
  size?: ControlSize;
  invalid?: boolean;
  /** Grow with the content up to `maxRows`. */
  autoResize?: boolean;
  minRows?: number;
  maxRows?: number;
  /** Show `used / maxLength`. Warns at 90%, turns danger past the limit. */
  counter?: boolean;
  containerClassName?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  {
    size = 'md',
    invalid = false,
    autoResize = false,
    minRows = 3,
    maxRows = 12,
    counter = false,
    className,
    containerClassName,
    disabled = false,
    value,
    defaultValue,
    onChange,
    maxLength,
    ...rest
  },
  ref,
) {
  const innerRef = useRef<HTMLTextAreaElement>(null);
  const [uncontrolled, setUncontrolled] = useState(() => String(defaultValue ?? ''));
  const current = value !== undefined ? String(value ?? '') : uncontrolled;

  const resize = useCallback(() => {
    const node = innerRef.current;
    if (!node || !autoResize) return;
    const style = getComputedStyle(node);
    const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.5;
    const ceiling = lineHeight * maxRows;
    node.style.height = 'auto';
    const next = Math.min(node.scrollHeight, ceiling);
    node.style.height = `${next}px`;
    node.style.overflowY = node.scrollHeight > ceiling ? 'auto' : 'hidden';
  }, [autoResize, maxRows]);

  useLayoutEffect(resize, [resize, current]);

  const used = current.length;
  const ratio = maxLength ? used / maxLength : 0;

  return (
    <div
      data-disabled={disabled ? '' : undefined}
      className={cn(
        fieldShell({ size, invalid, disabled, focus: 'within', multiline: true }),
        'flex-col items-stretch',
        PAD[size],
        containerClassName,
      )}
    >
      <textarea
        ref={composeRefs(ref, innerRef)}
        rows={minRows}
        disabled={disabled}
        maxLength={maxLength}
        aria-invalid={invalid || undefined}
        value={value}
        defaultValue={defaultValue}
        onChange={(event) => {
          if (value === undefined) setUncontrolled(event.target.value);
          onChange?.(event);
        }}
        className={cn(
          'w-full flex-1 resize-none bg-transparent text-inherit leading-normal',
          'placeholder:text-muted disabled:cursor-not-allowed',
          'focus-visible:outline-none!',
          autoResize && 'resize-none overflow-hidden',
          className,
        )}
        {...rest}
      />

      {counter ? (
        <div
          aria-live="polite"
          className={cn(
            'mt-1 shrink-0 self-end text-[length:var(--text-2xs)] tnum',
            ratio > 1 ? 'text-danger' : ratio >= 0.9 ? 'text-warning' : 'text-muted',
          )}
        >
          {maxLength ? `${used} / ${maxLength}` : used}
        </div>
      ) : null}
    </div>
  );
});
